import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const STATUSES = ["Open", "Answered", "Closed"];

export default function AdminTickets() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const { data: tickets = [] } = useQuery({
    queryKey: ["admin-tickets", filter],
    queryFn: async () => (await api.get("/admin/tickets", { params: filter !== "all" ? { status: filter } : {} })).data,
  });
  const [active, setActive] = useState(null);
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const [status, setStatus] = useState("Answered");
  const [busy, setBusy] = useState(false);

  const openTicket = async (t) => { const { data } = await api.get(`/admin/tickets/${t.id}`); setActive(data); setStatus(data.status === "Open" ? "Answered" : data.status); };

  const reply = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/tickets/${active.id}/reply`, { message: msg, status, internal });
      const { data } = await api.get(`/admin/tickets/${active.id}`);
      setActive(data); setMsg(""); setInternal(false);
      qc.invalidateQueries({ queryKey: ["admin-tickets"] });
      toast.success("Reply sent");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Support tickets</h1><p className="text-sm text-muted-foreground">{tickets.length} tickets</p></div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-6 space-y-3">
        {tickets.map((t) => (
          <button key={t.id} onClick={() => openTicket(t)} className="flex w-full items-center justify-between bs-card p-4 text-left hover:shadow-soft" data-testid={`admin-ticket-${t.id}`}>
            <div><div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{t.ticket_no}</span><Badge variant="outline">{t.category}</Badge><Badge variant="outline">{t.priority}</Badge></div><h3 className="mt-1 font-display font-bold">{t.subject}</h3><p className="text-xs text-muted-foreground">{t.user_email}</p></div>
            <Badge variant="secondary">{t.status}</Badge>
          </button>
        ))}
        {tickets.length === 0 && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No tickets.</p>}
      </div>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="flex w-full flex-col overflow-y-auto sm:max-w-lg">
          {active && (
            <>
              <SheetHeader><SheetTitle>{active.subject}</SheetTitle></SheetHeader>
              <p className="text-xs text-muted-foreground">{active.ticket_no} · {active.user_email}</p>
              <div className="mt-4 flex-1 space-y-3">
                {(active.messages || []).map((m, i) => (
                  <div key={i} className={`rounded-xl border border-border p-3 text-sm ${m.internal ? "bg-chart-4/10" : m.author_role === "client" ? "bg-secondary" : "bg-primary/10"}`}>
                    <p className="text-xs font-semibold text-muted-foreground">{m.author_name} · {m.internal ? "Internal note" : m.author_role}</p>
                    <p className="mt-1">{m.body}</p>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a reply..." data-testid="admin-ticket-reply" />
                <div className="flex items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={internal} onCheckedChange={setInternal} data-testid="admin-ticket-internal" /> Internal note</label>
                  <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                </div>
                <Button onClick={reply} className="w-full rounded-full" disabled={busy} data-testid="admin-ticket-send">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
